import { useState, useEffect, useRef, useCallback } from "react"
import { useTranslation, Trans } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Slider } from "@/components/ui/slider"
import {
  Play,
  Ratio,
  Type,
  Film,
  Layout,
  AlertCircle,
  X,
  Check,
  HelpCircle,
  Monitor,
  Loader2,
} from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Logo } from "@/components/common/Logo"
import { ImageWithFallback } from "@/components/common/ImageWithFallback"
import { FrameImageUpload } from "@/features/generator/components/FrameImageUpload"
import { TemplatePromptDisplay } from "@/features/generator/components/TemplatePromptDisplay"
import { useGeneratorStore } from "@/stores/generatorStore"
import { useAuthStore } from "@/stores/authStore"
import { useConfig } from "@/hooks/useConfig"
import { hasTemplateParameters } from "@/lib/template-parameters"
import {
  IMAGE_APP_URL,
  VIDEO_RATIO_OPTIONS,
  VIDEO_RESOLUTION_OPTIONS,
  type VideoRatio,
  type VideoResolution,
} from "@/lib/constants"
import { tDynamic } from "@/i18n"
import { localizeName, localizeDescription } from "@/lib/i18n-content"
import { cn } from "@/lib/utils"

export interface GeneratorSidebarProps {
  /** 모든 컨트롤 비활성화 (비인증 사용자용) */
  disabled?: boolean
  /** 커스텀 생성 핸들러 */
  onGenerate?: () => void
  /** 템플릿 탭 이동 시 콜백 (모바일 드로어 닫기용) */
  onNavigateAway?: () => void
  /** 추가 className */
  className?: string
}

/**
 * 사이드바 내부 컨텐츠 (프레임 이미지, 프롬프트, 설정, 버튼)
 * MobileSidebarDrawer에서 재사용됨
 */
export function GeneratorSidebarContent({
  disabled = false,
  onGenerate,
  onNavigateAway,
}: Omit<GeneratorSidebarProps, "className">) {
  const { t } = useTranslation()
  const {
    prompt,
    ratio,
    resolution,
    duration,
    frameImages,
    isSubmitting,
    selectedTemplate,
    templateRawPrompt,
    templateParameters,
    setPrompt,
    setRatio,
    setResolution,
    setDuration,
    setFirstFrame,
    setLastFrame,
    clearTemplate,
    setTemplateParameter,
  } = useGeneratorStore()

  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { data: config, isLoading: isConfigLoading } = useConfig()
  const outageEnabled = config?.outageNotice?.enabled === true
  const hasFrameInput = frameImages.firstFrame !== null
  // "직접 편집" 상태는 적용된 템플릿 객체에 묶는다. applySettings(템플릿 선택·재사용)마다
  // 새 객체가 되므로, 같은 템플릿을 다시 적용해도 파라미터 칩 모드로 열린다.
  const [directEditState, setDirectEditState] = useState<{
    template: typeof selectedTemplate
    enabled: boolean
  }>({
    template: selectedTemplate,
    enabled: false,
  })
  const directEditMode =
    directEditState.template === selectedTemplate ? directEditState.enabled : false
  const setDirectEditMode = useCallback(
    (enabled: boolean) => {
      setDirectEditState({ template: selectedTemplate, enabled })
    },
    [selectedTemplate],
  )
  const [isCooldown, setIsCooldown] = useState(false)
  const cooldownRef = useRef<ReturnType<typeof setTimeout>>(null)

  const handleGenerateWithCooldown = useCallback(() => {
    onGenerate?.()
    setIsCooldown(true)
    if (cooldownRef.current) clearTimeout(cooldownRef.current)
    cooldownRef.current = setTimeout(() => setIsCooldown(false), 3000)
  }, [onGenerate])

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearTimeout(cooldownRef.current)
    }
  }, [])

  const [hasScrollbar, setHasScrollbar] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const checkScrollbar = useCallback(() => {
    const el = textareaRef.current
    if (el) {
      setHasScrollbar(el.scrollHeight > el.clientHeight)
    }
  }, [])

  useEffect(() => {
    checkScrollbar()
  }, [prompt, directEditMode, checkScrollbar])

  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    const observer = new ResizeObserver(checkScrollbar)
    observer.observe(el)
    return () => observer.disconnect()
  }, [directEditMode, checkScrollbar])

  // 버튼 비활성화 조건
  const isButtonDisabled =
    disabled ||
    !prompt.trim() ||
    user?.hasUsage === false ||
    isSubmitting ||
    isCooldown ||
    isConfigLoading ||
    outageEnabled

  return (
    <div className="space-y-8 overflow-hidden">
      {/* Template Indicator (only when template is selected) */}
      {selectedTemplate && (
        <div className="space-y-3">
          <Label className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Layout className="w-4 h-4 text-primary" />
            {t('generator.template')}
          </Label>
          {/* 템플릿 모드: 가로형 카드 (썸네일 + 제목/설명 + X 버튼) */}
          <div className="relative flex gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
            {/* X 버튼 (우측 상단) */}
            {!disabled && (
              <button
                onClick={clearTemplate}
                className="absolute top-2 right-2 w-5 h-5 bg-slate-200 hover:bg-slate-300 rounded-full flex items-center justify-center transition-colors"
                title={t('generator.clearTemplate')}
              >
                <X className="w-3 h-3 text-slate-500" />
              </button>
            )}
            {/* 썸네일 */}
            <div className="w-14 h-14 rounded-md overflow-hidden shrink-0 bg-slate-200">
              {selectedTemplate.thumbnailUrl ? (
                <ImageWithFallback
                  src={selectedTemplate.thumbnailUrl}
                  alt={localizeName(selectedTemplate)}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Layout className="w-5 h-5 text-slate-400" />
                </div>
              )}
            </div>
            {/* 제목 + 설명 */}
            <div className="flex-1 min-w-0 pr-4">
              <p className="text-sm font-medium text-slate-800 truncate">
                {localizeName(selectedTemplate)}
              </p>
              {selectedTemplate.description && (
                <p className="text-xs text-slate-400 line-clamp-2 mt-0.5">
                  {localizeDescription(selectedTemplate)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Frame Image Upload */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <Film className="w-4 h-4 text-primary" />
          {t('generator.frameImage')}
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[200px]">
                {t('generator.frameImageTooltip')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Label>
        <FrameImageUpload
          firstFrame={frameImages.firstFrame}
          lastFrame={frameImages.lastFrame}
          onFirstFrameChange={setFirstFrame}
          onLastFrameChange={setLastFrame}
          disabled={disabled}
        />
        <p className="text-[13px] text-slate-400 text-center">
          <Trans
            i18nKey="generator.imageToolHint"
            components={{
              imageToolLink: (
                <a
                  href={IMAGE_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-primary hover:underline"
                />
              ),
            }}
          />
        </p>
      </div>

      {/* Prompt Input */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Type className="w-4 h-4 text-primary" />
            {t('generator.prompt')}
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" collisionPadding={16} className="max-w-[280px]">
                  {t('generator.promptTooltip')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Label>
          <span className="text-xs text-slate-400">
            {prompt.length}/1000
          </span>
        </div>
        {/* 템플릿 모드: 인라인 파라미터 편집 UI (직접 편집 모드가 아닐 때만) */}
        {templateRawPrompt && templateParameters && hasTemplateParameters(templateRawPrompt) && !directEditMode ? (
          <TemplatePromptDisplay
            rawTemplate={templateRawPrompt}
            parameterValues={templateParameters}
            onParameterChange={setTemplateParameter}
            onExitTemplateMode={() => setDirectEditMode(true)}
            disabled={disabled}
          />
        ) : (
        <div className="space-y-2 pb-2">
            <div className="relative">
              <Textarea
                ref={textareaRef}
                placeholder={t('generator.promptPlaceholder')}
                className="min-h-[216px] resize-y text-sm p-4 pr-10 border-slate-200 focus:border-primary focus-visible:ring-inset placeholder:text-slate-300"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                disabled={disabled}
              />
              {/* 프롬프트 삭제 버튼 */}
              {prompt.trim() && !disabled && (
                <button
                  type="button"
                  onClick={() => setPrompt("")}
                  className={`absolute top-3 w-6 h-6 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center transition-all ${hasScrollbar ? "right-5" : "right-3"}`}
                  title={t('generator.clearPrompt')}
                >
                  <X className="w-3.5 h-3.5 text-slate-500" />
                </button>
              )}
            </div>
            {/* 템플릿 가이드 문구 */}
            {!prompt.trim() && !selectedTemplate && (
              <p className="text-xs text-slate-400 text-center">
                <Trans
                  i18nKey="generator.firstTimeHint"
                  components={{
                    templateLink: (
                      <button
                        type="button"
                        className="font-semibold text-primary hover:underline"
                        onClick={() => {
                          navigate("/templates")
                          onNavigateAway?.()
                        }}
                      />
                    ),
                  }}
                />
              </p>
            )}
            {/* 모드 전환 버튼 */}
            {directEditMode && templateRawPrompt && !disabled && (
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-xs text-slate-500 hover:text-slate-700"
                  onClick={() => setDirectEditMode(false)}
                >
                  <Layout className="w-3 h-3 mr-1" />
                  {t('generator.editAsTemplate')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Video Settings */}
      <div className="space-y-4">
        {/* Ratio & Resolution - Single Row */}
        <div className="flex items-center gap-3">
          {/* Ratio */}
          <div className="flex items-center gap-2 flex-1">
            <Ratio className="w-4 h-4 text-primary shrink-0" />
            {hasFrameInput ? (
              <div className="flex h-9 flex-1 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-500">
                {t('generator.ratioAdaptive')}
              </div>
            ) : (
              <Select
                value={ratio}
                onValueChange={(v) => setRatio(v as VideoRatio)}
                disabled={disabled}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VIDEO_RATIO_OPTIONS.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.id} {tDynamic(r.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Resolution */}
          <div className="flex items-center gap-2 flex-1">
            <Monitor className="w-4 h-4 text-primary shrink-0" />
            <Select
              value={resolution}
              onValueChange={(v) => setResolution(v as VideoResolution)}
              disabled={disabled}
            >
              <SelectTrigger className="flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VIDEO_RESOLUTION_OPTIONS.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Duration Slider */}
        <div className="space-y-2 pb-1">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium text-slate-700">{t('generator.duration')}</Label>
            <span className="text-sm text-slate-500">{t('common.seconds', { count: duration })}</span>
          </div>
          <Slider
            value={[duration]}
            onValueChange={(v) => setDuration(v[0])}
            min={4}
            max={15}
            step={1}
            disabled={disabled}
          />
        </div>
      </div>

      {/* Usage Warning */}
      {user?.hasUsage === false && (
        <Alert variant="destructive" className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {t('generator.usageLimitReached')}
          </AlertDescription>
        </Alert>
      )}

      {/* Generate Button */}
      <div>
        <Button
          variant="brand"
          className="w-full h-12 text-lg font-semibold"
          size="lg"
          onClick={handleGenerateWithCooldown}
          disabled={isButtonDisabled}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              {t('generator.requesting')}
            </>
          ) : isCooldown ? (
            <>
              <Check className="mr-2 h-5 w-5" />
              {t('generator.requestComplete')}
            </>
          ) : (
            <>
              <Play className="mr-2 h-5 w-5" />
              {t('generator.generateVideo')}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

/**
 * 데스크톱 사이드바 (lg 이상에서만 표시)
 */
export function GeneratorSidebar({
  disabled = false,
  onGenerate,
  className,
}: GeneratorSidebarProps) {
  return (
    <aside
      className={cn(
        "w-[380px] xl:w-[420px] bg-white border-r border-slate-200 flex-col z-10 shadow-sm shrink-0",
        "hidden lg:flex", // 기본적으로 lg 이상에서만 표시
        className
      )}
    >
      <div className="h-16 flex items-center px-6 border-b border-slate-100">
        <Link to="/">
          <Logo />
        </Link>
      </div>

      <ScrollArea className="flex-1 px-6 py-6">
        <GeneratorSidebarContent
          disabled={disabled}
          onGenerate={onGenerate}
        />
      </ScrollArea>
    </aside>
  )
}
